/* :Page:NewMotor: Car GDT Info page */
import { unwrapResult } from "@reduxjs/toolkit";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import infoicon from "../../../../../assets/images/Informationcircle.svg";
import Button from "../../../../../components/common/Button/Button";
import Input from "../../../../../components/common/Input/Input";
import SelectInput from "../../../../../components/common/selectInput/selectInput";
import SingleDatePicker from "../../../../../components/common/SingleDatePicker/SingleDatePicker";
import TitleDescription from "../../../../../components/newMotorPolicy/titleDescription/titleDescription";
import useLanguageTranslate from "../../../../../hooks/useLanguageTranslate";
import useMotorFlow from "../../../../../hooks/useMotorFlow";
import useMotorForm from "../../../../../hooks/useMotorFormData";
import useValidation from "../../../../../hooks/useValidation";
import { fetchBanksList } from "../../../../../redux/features/constants/banksListSlice";
import { fetchCarsList, selectCars, selectCarsLoader } from "../../../../../redux/features/constants/carsListSlice";
import {
  gdtRetrievePlateTypesListResponse,
  retrieveVehicleFromGDT,
} from "../../../../../redux/features/motor/gdtSlice";
import BottomStickyBtnLayout from "../../Layouts/BottomStickyBtnLayout";
import NewMotorJourneyLayout from "../../Layouts/NewMotorJourneyLayout";
import CarInfoTable from "./components/carInfoTable";
import { CAR_INFO_PLATE_NUMBER, car_info_table_data } from "./data";
import NumberPlatesTypesPopup from "./numberPlatesTypesPopup";

const FALLBACK_PLATE_TYPES = [
  { PlateTypeCode: "01", ArabicName: "خصوصي", EnglishName: "PRIVATE" }
];

const CarInfoPlateNumber = () => {
  const dispatch = useDispatch();
  const [vehicleInformation, setVehicleInformation] = useState();
  const [isMappedWithGDT, setMappedWithGDT] = useState(false);
  const banksList = useSelector((state) => state.banksList);
  const translate = useLanguageTranslate();

  const [showCarInfo, setShowcarInfo] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [formData, setFormData] = useState({});
  const { errors, validateField } = useValidation();
  const { handleNextSubstep, handleFlowChange } = useMotorFlow();
  const { t } = useTranslation();
  const { saveMotorDataInRedux } = useMotorForm(
    CAR_INFO_PLATE_NUMBER,
    setFormData
  );

  const carsList = useSelector(selectCars);
  const carsListLoading = useSelector(selectCarsLoader);

  const plateTypeList = useSelector(gdtRetrievePlateTypesListResponse)
  const effectivePlateTypeList = plateTypeList?.length > 0 ? plateTypeList : FALLBACK_PLATE_TYPES;

  const { minDate, maxDate } = useMemo(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const minDate = `${yyyy}-${mm}-${dd}`;

    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + 90);
    const yyyyF = futureDate.getFullYear();
    const mmF = String(futureDate.getMonth() + 1).padStart(2, "0");
    const ddF = String(futureDate.getDate()).padStart(2, "0");
    const maxDate = `${yyyyF}-${mmF}-${ddF}`;

    return { minDate, maxDate };
  }, []);


  const continueClickHandler = async () => {
    const isPlateNumber = validateField(
      formData.plateNumber,
      "plateNumber",
      "required"
    );

    if (isPlateNumber) {
      // Phase 1: GDT lookup (first click, not in manual mode yet)
      if (!showCarInfo && !isManualEntry) {
        try {
          const gdtPayload = {
            PlateOrChassisNo: formData.plateNumber,
            PlateType: formData.plateType ? formData.plateType : "01"
          };

          const resultAction = await dispatch(retrieveVehicleFromGDT(gdtPayload));
          const vehicleData = await unwrapResult(resultAction);

          console.log("Vehicle data retrieved:", vehicleData.VehicleDetails);

          if (vehicleData.VehicleDetails.TCSVehicleMakeCode =="" || vehicleData.VehicleDetails.TCSVehicleModelCode =="") {
            setMappedWithGDT(false);
          } else {
            setMappedWithGDT(true);
          }
          setVehicleInformation(vehicleData.VehicleDetails);
          setShowcarInfo(true);
          return;
        } catch (error) {
          console.log("GDT lookup failed, switching to manual entry:", error);
          toast.info(translate(
            "Could not retrieve vehicle details. Please enter details manually.",
            "تعذر استرداد تفاصيل المركبة. يرجى إدخال التفاصيل يدوياً."
          ));
          setIsManualEntry(true);
          setMappedWithGDT(false);
          return;
        }
      }

      // Phase 2: Process and proceed (second click with GDT data OR manual entry)
      try {
        console.log('Mapped to GDT ? ', isMappedWithGDT);

        if (!banksList.banks) {
          await dispatch(fetchBanksList());
        }

        if (!carsList) {
          await dispatch(fetchCarsList());
        }

        if (!carsListLoading) {
          let prorataStartDate;
          let prorataDates;

          if (isManualEntry) {
            // Manual entry: user enters policy start date directly
            const isStartDate = validateField(formData.policyStartDate, "policyStartDate", "required");
            if (!isStartDate) return;

            const startDateObj = new Date(formData.policyStartDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const diffInTime = startDateObj.getTime() - today.getTime();
            const diffInDays = Math.ceil(diffInTime / (1000 * 60 * 60 * 24));

            if (diffInDays < 0) {
              toast.error(translate("Policy start date cannot be in the past", "لا يمكن أن يكون تاريخ بدء الوثيقة في الماضي"));
              return;
            } else if (diffInDays > 90) {
              toast.error(translate("Policy start date must be within 90 days", "يجب أن يكون تاريخ بدء الوثيقة خلال 90 يومًا"));
              return;
            }

            prorataStartDate = startDateObj.toISOString().split('T')[0];
            // End date = start date + 1 year
            const endDateObj = new Date(startDateObj);
            endDateObj.setFullYear(endDateObj.getFullYear() + 1);
            prorataDates = endDateObj.toISOString().split('T')[0];
          } else {
            // GDT path: derive dates from registration expiry
            const regExpiryDate = vehicleInformation.RegistrationExpiryDate;
            prorataDates = getProrataDates(regExpiryDate);
            prorataStartDate = getProrataDateFrom(regExpiryDate);

            console.log("Registration expiry date: ", regExpiryDate);
            console.log("Processed vehicle date:", prorataDates);

            if (prorataStartDate =="MoreThan90") {
              toast.error("GDT Registration expiry date must be less than 90 days ahead");
              return;
            } else if (prorataStartDate =="Backdated") {
              toast.error("GDT Registration is expired");
              return;
            }
          }

          const updatedFormData = {
            ...formData,
            newEndDate: prorataDates,
            newFromDate: prorataStartDate,
            isMappedWithGDT: isMappedWithGDT,
            TCSMakeCode: showCarInfo && vehicleInformation?.TCSVehicleMakeCode !="" ? vehicleInformation.TCSVehicleMakeCode : "",
            TCSModelCode: showCarInfo && vehicleInformation?.TCSVehicleModelCode !="" ? vehicleInformation.TCSVehicleModelCode : "",
            vehiclePlateType: showCarInfo ? vehicleInformation.PlateTypeCode : (formData.plateType ? formData.plateType : "01")
          };

          console.log('Mapped to GDT ? ', isMappedWithGDT);
          saveMotorDataInRedux(updatedFormData);
          handleNextSubstep();
        }
      } catch (error) {
        toast.error("Error processing vehicle data");
      }
    }
  };

  // Pro-rata function for end date
  const getProrataDates = (vehicleDate) => {
    if (!vehicleDate) {
      console.error("Vehicle date is missing");
      return null;
    }

    const regExpDate = new Date(vehicleDate);
    const currentDate = new Date();

    // Validate if regExpDate is a valid date
    if (isNaN(regExpDate)) {
      console.error("Invalid vehicle date");
      return null;
    }

    // Calculate the difference in months between two dates
    const getMonthDifference = (startDate, endDate) => {
      return (
        (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        (endDate.getMonth() - startDate.getMonth())
      );
    };

    let newEndDate;

    // Logic for when reg. exp. date is greater than current date
    if (regExpDate > currentDate) {
      const monthDiff = getMonthDifference(currentDate, regExpDate);

      if (monthDiff < 6) {
        // Add 1 year if the difference is less than 6 months
        newEndDate = new Date(
          regExpDate.setFullYear(regExpDate.getFullYear() + 1)
        );
      } else if (monthDiff >= 6) {
        // Keep the reg. exp. date if the difference is 6 months or more
        newEndDate = regExpDate;
      }
    }

    // Logic for when reg. exp. date is less than current date
    else if (regExpDate < currentDate) {
      const monthDiff = getMonthDifference(regExpDate, currentDate);

      const regExpDatePlusOneYear = new Date(regExpDate);
      regExpDatePlusOneYear.setFullYear(currentDate.getFullYear() + 1);

      if (monthDiff < 6) {
        // Add 2 years if the difference is less than 6 months
        newEndDate = new Date(
          regExpDate.setFullYear(currentDate.getFullYear() + 2)
        );
      } else if (monthDiff >= 6) {
        // Add 1 year if the difference is 6 months or more
        newEndDate = new Date(
          regExpDate.setFullYear(currentDate.getFullYear() + 1)
        );
      }
    }

    // Format the result date to YYYY-MM-DD
    if (newEndDate) {
      return newEndDate.toISOString().split("T")[0];
    }

    return null; // return null if no valid newEndDate
  };

  // Pro-rata start data
  // Pro-rata start date
  const getProrataDateFrom = (vehicleDate) => {
    if (!vehicleDate) {
      console.error("Vehicle date is missing");
      return null;
    }

    const regExpDate = new Date(vehicleDate);
    const today = new Date();

    // Check if the vehicle date is invalid or backdated
    if (isNaN(regExpDate.getTime()) || regExpDate < today) {
      console.error("Vehicle date is invalid or backdated");
      return "Backdated";
    }

    // Calculate the difference in days
    const diffInTime = regExpDate.getTime() - today.getTime();
    const diffInDays = Math.ceil(diffInTime / (1000 * 60 * 60 * 24));

    // If regExpDate is more than 90 days ahead
    if (diffInDays > 90) {
      console.error("Vehicle date is more than 90 days ahead");
      return "MoreThan90";
    }

    // Add one day to regExpDate for the startDate
    const startDate = new Date(regExpDate);
    startDate.setDate(startDate.getDate() + 1);

    // Format the result date to YYYY-MM-DD
    if (startDate) {
      return startDate.toISOString().split("T")[0];
    }

    return startDate;
  };

  const [showPopup, setShowPopup] = useState(false);
  const togglePopup = () => {
    setShowPopup((state) => !state);
  };

  const handleDateChange = (name, dateValue) => {
    setFormData((prev) => ({
      ...prev,
      [name]: dateValue,
    }));
  };

  const handleChange = (e) => {
    // Remove table with car info on plate number input change
    setShowcarInfo(false);
    setIsManualEntry(false);
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    switch (name) {
      case "plateNumber":
        validateField(value, name, "required");
        break;
      case "plateType":
        validateField(value, name, "select");
        break;
      default:
        break;
    }
  };

  console.log(formData, "form data");

  return (
    <NewMotorJourneyLayout>
      <NumberPlatesTypesPopup show={showPopup} onClose={togglePopup} />
      <BottomStickyBtnLayout btn_onClick={continueClickHandler}>
        <div className="max-w-[100%] w-[500px] mx-auto bg-white max767:p-5 max767:pt-0">
          <TitleDescription
            title={t(
              "motorInsurance.newMotorInsuranceFlow.carInfoPlateNumber.title"
            )}
            description={t(
              "motorInsurance.newMotorInsuranceFlow.carInfoPlateNumber.description"
            )}
          />
          <Input
            name="plateNumber"
            type="text"
            label={t(
              "motorInsurance.newMotorInsuranceFlow.carInfoPlateNumber.plateNumber"
            )}
            value={formData?.plateNumber}
            error={errors.plateNumber}
            handleChange={handleChange}
          />
          <SelectInput
            name="plateType"
            default_placeholder="PRIVATE - خصوصي "
            formData={formData}
            value={formData?.plateType}
            options_data={effectivePlateTypeList?.map((plate) => ({
              value: plate.PlateTypeCode,
              label: `${plate.EnglishName} - ${plate.ArabicName}`,
            })) || []}
            label={t(
              "motorInsurance.newMotorInsuranceFlow.carInfoPlateNumber.plateType"
            )}
            error={errors?.plateType}
            handleChange={handleChange}
          />
          {/* <Input
            name="plateType"
            default_placeholder="Private"
            placeHolder="Private"
            formData={formData}
            value={formData?.plateType}
            // options_data={[
            //   { value: "Private", label: "Private" },
            // ]}
            label={t(
              "motorInsurance.newMotorInsuranceFlow.carInfoPlateNumber.plateType"
            )}
            error={errors?.plateType}
            handleChange={handleChange}
            disabled={true}

          /> */}
          <div className="alertBox justify-between w-full flex items-start gap-4 px-4 py-3 bg-[#F6F8F9] mb-6 rounded-md items-center">
            <div className="flex gap-1 max767:w-[70%] max767:items-start">
              <img src={infoicon} alt="info" className="infoIcon mr-2" />
              <p className="text-[#252C32] text-sm not-italic font-semibold leading-6 tracking-[-0.084px]">
                {t(
                  "motorInsurance.newMotorInsuranceFlow.carInfoPlateNumber.discoverPlateTypes"
                )}
              </p>
            </div>
            <Button
              label={t(
                "motorInsurance.newMotorInsuranceFlow.carInfoPlateNumber.showMe"
              )}
              onSubmit={togglePopup}
              className="showme flex flex-col items-center gap-2.5 bg-[#55C6EF] text-[#48535B] text-center text-sm not-italic font-semibold leading-6 tracking-[-0.084px] px-3 py-1 rounded-md max767:w-[85px] max767:align-top"
            />
          </div>
          {showCarInfo && (
            <div className="carInfoCard bg-[#F6F8F9] rounded-2xl flex flex-col items-start gap-2 self-stretch p-4 mb-6">
              <h2 className="text-[#1A2024] text-base not-italic font-semibold leading-6 tracking-[-0.16px]">
                {translate("We Found Your Car!","لقد وجدنا سيارتك!")}
              </h2>
              <p className="text-[#1A2024] text-sm not-italic font-normal leading-5 tracking-[-0.14px]">
                {translate("Please confirm the details below are correct for your vehicle.","يرجى التأكد من صحة التفاصيل أدناه لسيارتك.")}
              </p>
              <div className="mobile-scroll max-390:overflow-x-scroll w-full">
                <CarInfoTable table_data={car_info_table_data(vehicleInformation)} />
              </div>
            </div>
          )}
          {isManualEntry && (
            <div className="manualEntryCard bg-[#FFF8E1] rounded-2xl flex flex-col items-start gap-2 self-stretch p-4 mb-6">
              <h2 className="text-[#1A2024] text-base not-italic font-semibold leading-6 tracking-[-0.16px]">
                {translate("Enter Vehicle Details Manually","أدخل تفاصيل المركبة يدوياً")}
              </h2>
              <p className="text-[#1A2024] text-sm not-italic font-normal leading-5 tracking-[-0.14px]">
                {translate(
                  "We couldn't retrieve your vehicle details. Please enter the policy start date.",
                  "تعذر استرداد تفاصيل مركبتك. يرجى إدخال تاريخ بدء الوثيقة."
                )}
              </p>
              <div className="w-full mt-2">
                <SingleDatePicker
                  label={translate("Policy Start Date", "تاريخ بدء الوثيقة")}
                  value={formData.policyStartDate}
                  onChange={(value) => handleDateChange("policyStartDate", value)}
                  placeholder={translate("dd/mm/yyyy", "يوم/شهر/سنة")}
                  error={errors?.policyStartDate}
                  minDate={minDate}
                  maxDate={maxDate}
                  name="policyStartDate"
                />
              </div>
            </div>
          )}
        </div>
      </BottomStickyBtnLayout>
    </NewMotorJourneyLayout>
  );
};

export default CarInfoPlateNumber;
